import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Search, Plus, Pencil, Trash2, X, Home, Building2, MapPin } from "lucide-react";
import {
  getMasterLabs, createMasterLab, updateMasterLab, deleteMasterLab, getMasterLabTests,
} from "../../api/api";
import DataTable from "../../components/DataTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";

const EMPTY = {
  name: "", code: "", phone: "",
  address: { line: "", city: "", state: "", pincode: "" },
  pincodes: [], test_codes: [],
  supports_home_collection: true, supports_center_visit: true, active: true,
};

/** Chip-style entry for a list of pincodes — typing 6 digits commits one. */
function PincodeInput({ value = [], onChange }) {
  const [draft, setDraft] = useState("");

  const add = (raw) => {
    const code = raw.replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6 || value.includes(code)) return;
    onChange([...value, code]);
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          inputMode="numeric"
          value={draft}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 6);
            setDraft(v);
            if (v.length === 6) { add(v); setDraft(""); }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); add(draft); setDraft(""); }
            // Backspace on an empty field removes the last chip
            if (e.key === "Backspace" && !draft && value.length) onChange(value.slice(0, -1));
          }}
          placeholder="Type a 6-digit pincode"
        />
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((p) => (
            <span key={p} className="inline-flex items-center gap-1 rounded-full border border-border bg-card py-0.5 pl-2.5 pr-1 text-xs">
              {p}
              <button type="button" aria-label={`Remove ${p}`}
                onClick={() => onChange(value.filter((x) => x !== p))}
                className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function LabForm({ open, onOpenChange, lab, onSaved, allTests }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [limitTests, setLimitTests] = useState(false);
  const editing = !!lab;

  useEffect(() => {
    if (!open) return;
    const base = lab ? { ...EMPTY, ...lab, address: { ...EMPTY.address, ...(lab.address || {}) } } : EMPTY;
    setForm(base);
    setLimitTests((base.test_codes || []).length > 0);
  }, [open, lab]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));

  const noCollection = !form.supports_home_collection && !form.supports_center_visit;
  const noPincode = form.pincodes.length === 0;

  const submit = async (e) => {
    e.preventDefault();
    if (noCollection || noPincode) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        phone: form.phone || "",
        address: form.address,
        pincodes: form.pincodes,
        // Empty means "runs the full catalogue" — matches the model's contract.
        test_codes: limitTests ? form.test_codes : [],
        supports_home_collection: !!form.supports_home_collection,
        supports_center_visit: !!form.supports_center_visit,
        active: !!form.active,
      };
      if (editing) await updateMasterLab(lab._id, payload);
      else await createMasterLab(payload);
      toast.success(editing ? "Lab updated." : "Lab added.");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.message || "Could not save the lab.");
    } finally {
      setSaving(false);
    }
  };

  const toggleTest = (code) =>
    set("test_codes", form.test_codes.includes(code)
      ? form.test_codes.filter((c) => c !== code)
      : [...form.test_codes, code]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit lab" : "Add lab"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-3 sm:grid-cols-[1fr_9rem]">
            <div className="space-y-1.5">
              <Label htmlFor="l-name">Lab name *</Label>
              <Input id="l-name" value={form.name} onChange={(e) => set("name", e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="l-code">Code *</Label>
              <Input id="l-code" value={form.code} onChange={(e) => set("code", e.target.value)}
                className="font-mono uppercase" required />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="l-phone">Phone</Label>
              <Input id="l-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="l-city">City</Label>
              <Input id="l-city" value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="l-line">Address</Label>
            <Input id="l-line" value={form.address.line} onChange={(e) => setAddr("line", e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Pincodes served *</Label>
            <PincodeInput value={form.pincodes} onChange={(v) => set("pincodes", v)} />
            {noPincode && (
              <p className="text-xs text-destructive">
                Add at least one — an order only offers labs covering the patient&rsquo;s pincode.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Collection methods *</Label>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.supports_home_collection}
                  onCheckedChange={(v) => set("supports_home_collection", v)} />
                <Home className="h-3.5 w-3.5 text-muted-foreground" /> Home collection
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.supports_center_visit}
                  onCheckedChange={(v) => set("supports_center_visit", v)} />
                <Building2 className="h-3.5 w-3.5 text-muted-foreground" /> Center visit
              </label>
            </div>
            {noCollection && (
              <p className="text-xs text-destructive">Pick at least one, or the lab can never be selected.</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={limitTests} onCheckedChange={setLimitTests} />
              Restrict to specific tests
            </label>
            <p className="text-xs text-muted-foreground">
              {limitTests
                ? `${form.test_codes.length} of ${allTests.length} selected. Orders containing anything else will skip this lab.`
                : "Currently offers the full catalogue."}
            </p>
            {limitTests && (
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-border p-2">
                {allTests.map((t) => (
                  <label key={t._id} className="flex items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted/50">
                    <Checkbox checked={form.test_codes.includes(t.code)}
                      onCheckedChange={() => toggleTest(t.code)} />
                    <span className="truncate">{t.name}</span>
                    <span className="ml-auto shrink-0 font-mono text-[11px] text-muted-foreground">{t.code}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.active} onCheckedChange={(v) => set("active", v)} />
            Accepting new orders
          </label>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || noCollection || noPincode}>
              {saving ? "Saving…" : editing ? "Save changes" : "Add lab"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function LabMaster() {
  const [labs, setLabs] = useState([]);
  const [allTests, setAllTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, numberOfPage: 1 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getMasterLabs({ search: search || undefined, page, limit: 20 })
      .then((res) => {
        setLabs(res.data);
        setMeta({ total: res.total, numberOfPage: res.numberOfPage });
      })
      .catch((err) => toast.error(err?.message || "Could not load labs."))
      .finally(() => setLoading(false));
  }, [search, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    getMasterLabTests({ limit: 500, active: "true" })
      .then((res) => setAllTests(res.data || []))
      .catch(() => setAllTests([]));
  }, []);

  const remove = async (lab) => {
    try {
      const res = await deleteMasterLab(lab._id);
      toast.success(res.message || "Lab removed.");
      load();
    } catch (err) {
      toast.error(err?.message || "Could not remove the lab.");
    }
  };

  const columns = [
    {
      key: "name",
      header: "Lab",
      render: (l) => (
        <div className="min-w-0">
          <p className="truncate font-medium leading-tight">{l.name}</p>
          <p className="truncate text-xs leading-tight text-muted-foreground">
            <span className="font-mono">{l.code}</span>
            {l.address?.city ? ` · ${l.address.city}` : ""}
          </p>
        </div>
      ),
    },
    {
      key: "coverage",
      header: "Coverage",
      render: (l) => (
        <div className="flex items-start gap-1.5">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <p className="text-sm leading-tight">
              {l.pincodes?.length || 0} pincode{l.pincodes?.length === 1 ? "" : "s"}
            </p>
            <p className="truncate text-xs leading-tight text-muted-foreground">
              {(l.pincodes || []).slice(0, 4).join(", ")}
              {(l.pincodes?.length || 0) > 4 ? ` +${l.pincodes.length - 4}` : ""}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "methods",
      header: "Methods",
      render: (l) => (
        <div className="flex flex-wrap gap-1">
          {l.supports_home_collection && (
            <Badge variant="outline" className="gap-1 font-normal"><Home className="h-3 w-3" /> Home</Badge>
          )}
          {l.supports_center_visit && (
            <Badge variant="outline" className="gap-1 font-normal"><Building2 className="h-3 w-3" /> Center</Badge>
          )}
        </div>
      ),
    },
    {
      key: "tests",
      header: "Catalogue",
      render: (l) => (
        <span className="text-sm text-muted-foreground">
          {l.test_codes?.length ? `${l.test_codes.length} tests` : "Full catalogue"}
        </span>
      ),
    },
    {
      key: "orders",
      header: "Orders",
      render: (l) => <span className="text-sm tabular-nums">{l.order_count ?? 0}</span>,
    },
    {
      key: "active",
      header: "Status",
      render: (l) => (
        <Badge variant={l.active ? "secondary" : "outline"}>{l.active ? "Active" : "Paused"}</Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (l) => (
        <div className="flex items-center justify-end gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" aria-label={`Edit ${l.name}`}
            onClick={() => { setEditing(l); setFormOpen(true); }}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            aria-label={`Remove ${l.name}`} onClick={() => remove(l)}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Partner Labs</h2>
          <p className="text-sm text-muted-foreground">
            Which labs serve which areas, and what they can run
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
          <Plus className="h-4 w-4" /> Add lab
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by name, code, city, or pincode…"
          className="h-9 pl-9"
        />
      </div>

      {!loading && (
        <p className="text-sm text-muted-foreground">
          {meta.total} lab{meta.total === 1 ? "" : "s"}
        </p>
      )}

      <DataTable
        columns={columns}
        data={labs}
        loading={loading}
        emptyMessage={search ? "No labs match that search." : "No partner labs yet."}
        emptyAction={
          <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="h-4 w-4" /> Add the first lab
          </Button>
        }
        page={page}
        numberOfPage={meta.numberOfPage}
        onPageChange={setPage}
        rowKey={(l) => l._id}
      />

      <LabForm
        open={formOpen}
        onOpenChange={setFormOpen}
        lab={editing}
        allTests={allTests}
        onSaved={load}
      />
    </div>
  );
}
